﻿<script lang="ts">
	import './layout.css';
	import { onMount } from 'svelte';
	import { onNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import Icon from '@iconify/svelte';

	onNavigate((navigation) => {
		if (!document.startViewTransition) return;
		return new Promise((resolve) => {
			document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});
	import favicon from '$lib/assets/favicon.svg';
	import { watchDayRollover } from '$lib/daily';
	import { readSettings, applyTheme, applyDimBackground, type Theme } from '$lib/settings';
	import { THEME_VIDEOS, THEME_PREVIEWS } from '$lib/themes';
	import { getOrCreateUserId, pruneStaleCoopRooms } from '$lib/coop.svelte';
	import { getFriends } from '$lib/friends-global.svelte';
	import type { GameType, PlayMode } from '$lib/friends.svelte';
	import { renewUsername } from '$lib/directory';
	import { getStoredPin, scheduleSync, syncNow } from '$lib/cloud-sync';
	import bundledVersion from '$lib/version.json';
	import { publishMyLeaderScores, scheduleLeaderScorePublish } from '$lib/leaders';
	import { migrateLegacyStorageKeys } from '$lib/migrate-storage';
	import { btn } from '$lib/actions';
	import TopNav from '$lib/components/shared/TopNav.svelte';
	import BottomNav from '$lib/components/shared/BottomNav.svelte';
	import Toasts from '$lib/components/shared/Toasts.svelte';
	import Splash from '$lib/components/shared/Splash.svelte';
	import GameInviteModal from '$lib/components/shared/GameInviteModal.svelte';

	let { children } = $props();

	let theme = $state<Theme>('dark');
	let disableVideo = $state(false);
	let splashKey = $state(0);
	let offline = $state(false);
	let offlineBannerDismissed = $state(false);
	let showOfflineBanner = $derived(offline && !offlineBannerDismissed);

	const ROUTE_TO_GAME: Record<string, GameType> = {
		'/word-guess': 'word-guess',
		'/word-build': 'word-build',
		'/word-group': 'word-group',
		'/word-cross': 'word-cross',
		'/word-cross-m': 'word-cross-m',
		'/word-find': 'word-find',
		'/word-crypto': 'word-crypto',
		'/word-speed': 'word-speed',
		'/num-fill': 'num-fill',
		'/word-tower': 'word-tower'
	};

	$effect(() => {
		const gameType = ROUTE_TO_GAME[page.url.pathname] ?? null;
		const playMode: PlayMode | null = gameType ? (page.url.searchParams.has('coop') ? 'Co-op' : 'Solo') : null;
		getFriends(getOrCreateUserId()).setPresence(gameType, playMode);
	});

	const THEME_OVERLAYS: Record<Theme, string> = {
		hearth: 'bg-amber-900/30',
		galactic: 'bg-black/60',
		wizardry: 'bg-indigo-950/50',
		overgrown: 'bg-black/50',
		outbreak: 'bg-stone-900/60',
		catchers: 'bg-indigo-950/40',
		noir: 'bg-black/40',
		dark: '',
		light: ''
	};

	onMount(() => {
		// Self-heal stale-deploy failures: after a new deploy the old cached
		// app shell may request JS chunks whose hashed names no longer exist,
		// so the server returns HTML and the dynamic import throws. Reload
		// AT MOST ONCE per browser session — the flag is intentionally never
		// cleared here, so a still-broken build shows the error instead of
		// reloading forever (the layout shell always mounts fine, so clearing
		// on mount would loop endlessly).
		const RELOAD_KEY = 'chunk-reload-attempted';
		window.addEventListener('vite:preloadError', (e) => {
			e.preventDefault();
			if (sessionStorage.getItem(RELOAD_KEY)) return;
			sessionStorage.setItem(RELOAD_KEY, '1');
			window.location.reload();
		});

		// Migrate any legacy GameKey strings ('wordle' → 'word-guess', etc.) in
		// localStorage from earlier builds. Idempotent; safe to run every boot.
		migrateLegacyStorageKeys();

		// One-time progress reset for the rebrand (legacy names → Word Guess, etc.).
		// Wipes progress/state/completions/active-coop but PRESERVES settings
		// (username, avatar, theme, PIN). Runs once per browser, then a flag
		// is left so subsequent boots are no-ops.
		try {
			const RESET_FLAG = 'bubble-games-reset-rebrand-2026-05';
			if (localStorage.getItem(RESET_FLAG) !== '1') {
				localStorage.removeItem('bubble-games-progress');
				localStorage.removeItem('bubble-games-state');
				localStorage.removeItem('bubble-games-completions');
				localStorage.removeItem('bubble-games-active-coop');
				localStorage.setItem(RESET_FLAG, '1');
			}
		} catch {
			/* private mode / quota — skip */
		}

		const settings = readSettings();
		applyDimBackground(settings.dimBackground);
		applyTheme(settings.theme);

		// Drop any active-coop entries from previous puzzle days.
		pruneStaleCoopRooms();

		// Open the shared friends socket so badges + invites work app-wide
		const userId = getOrCreateUserId();
		getFriends(userId);
		// Renew username TTL so it doesn't expire while the app is in use
		renewUsername(userId);
		void publishMyLeaderScores(userId);
		theme = settings.theme;
		disableVideo = settings.disableVideo;

		let lastDimBackground = settings.dimBackground;
		const onStorage = () => {
			const s = readSettings();
			if (s.dimBackground !== lastDimBackground) {
				lastDimBackground = s.dimBackground;
				applyDimBackground(s.dimBackground);
			}
			if (s.disableVideo !== disableVideo) disableVideo = s.disableVideo;
			if (s.theme !== theme) {
				theme = s.theme;
				applyTheme(s.theme);
				try {
					sessionStorage.removeItem('bubble-splash-shown');
				} catch {}
				splashKey++;
			}
		};

		window.addEventListener('storage', onStorage);

		const updateOnlineStatus = () => {
			const nextOffline = !navigator.onLine;
			if (!nextOffline || nextOffline !== offline) {
				offlineBannerDismissed = false;
			}
			offline = nextOffline;
		};
		updateOnlineStatus();
		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		// Cloud sync — debounced sync whenever cloud-synced keys change.
		const CLOUD_KEYS = new Set(['bubble-games-progress', 'bubble-games-state', 'bubble-games-completions', 'bubble-games-settings', 'bubble-games-active-coop']);
		const LEADER_KEYS = new Set(['bubble-games-progress', 'bubble-games-completions', 'bubble-games-settings']);
		const origSetItem = Storage.prototype.setItem;
		Storage.prototype.setItem = function (key: string, value: string) {
			origSetItem.call(this, key, value);
			if (this === localStorage && CLOUD_KEYS.has(key) && getStoredPin()) {
				scheduleSync();
			}
			if (this === localStorage && LEADER_KEYS.has(key)) {
				scheduleLeaderScorePublish(userId);
			}
		};
		// Initial sync on app boot if PIN configured.
		if (getStoredPin()) void syncNow();

		const stopWatcher = watchDayRollover(() => window.location.reload());

		// Deep link handling — when the OS opens our app via a https://bubble-games-eight.vercel.app/...
		// link (Android App Link) or capacitor:// scheme, route to the in-app equivalent.
		let removeAppListener: (() => void) | null = null;
		(async () => {
			try {
				const { Capacitor } = await import('@capacitor/core');
				if (!Capacitor.isNativePlatform()) return;
				const { App } = await import('@capacitor/app');
				const { goto } = await import('$app/navigation');
				const handle = await App.addListener('appUrlOpen', (event) => {
					try {
						const incoming = new URL(event.url);
						const target = `${incoming.pathname}${incoming.search}${incoming.hash}`;
						if (target && target !== '/') goto(target);
					} catch {
						/* ignore malformed urls */
					}
				});
				removeAppListener = () => handle.remove();

				// Native update check — compare installed version against published version.json.
				// If the installed build is >= the published build, force the store back to
				// false: otherwise a banner triggered before a Play Store update can stay
				// stuck after the user is on the latest (e.g. when a rollout completes mid-
				// session and the store flag was already set during an earlier launch).
				// Stop relying on @capacitor/app's getInfo().build — on at least some
				// devices (v8.1.0) it's been observed returning the versionName
				// ("1.0") instead of the versionCode, which makes the comparison
				// permanently flag an update. The build-time versionCode is already
				// bundled into the APK via static/version.json, so just import that
				// directly as the "local" version of record.
				try {
					const res = await fetch('https://bubblegames.app/version.json?ts=' + Date.now(), { cache: 'no-store' });
					if (res.ok) {
						const data = (await res.json()) as { android?: { versionCode?: number } };
						const remoteCode = data?.android?.versionCode;
						const localCode = bundledVersion?.android?.versionCode;
						const { updateAvailable } = await import('$lib/update-status');
						if (typeof remoteCode === 'number' && typeof localCode === 'number' && remoteCode > localCode) {
							updateAvailable.set(true);
						} else {
							updateAvailable.set(false);
						}
					}
				} catch {
					/* offline or version.json missing — skip */
				}
			} catch {
				/* @capacitor/app not available — running in browser */
			}
		})();

		// Register the service worker manually, but only outside Capacitor.
		// The native WebView loads from https://localhost/ and cannot fetch
		// service-worker.js, which throws on every launch.
		(async () => {
			try {
				const { Capacitor } = await import('@capacitor/core');
				if (Capacitor.isNativePlatform()) return;
				if ('serviceWorker' in navigator) {
					const hadController = !!navigator.serviceWorker.controller;
					await navigator.serviceWorker.register('/service-worker.js', { type: 'module' });
					if (hadController) {
						const { updateAvailable } = await import('$lib/update-status');
						navigator.serviceWorker.addEventListener('controllerchange', () => {
							updateAvailable.set(true);
						});
					}
				}
			} catch {
				/* ignore — registration is best-effort, app still works */
			}
		})();

		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
			stopWatcher();
			removeAppListener?.();
		};
	});
</script>

<svelte:head>
	<link rel="preconnect" href="https://fonts.googleapis.com" />
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
	<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cinzel:wght@700&family=Oswald:wght@700&family=Bebas+Neue&family=Fredoka:wght@600&family=Merriweather:wght@400;700&display=swap" />
	<link rel="icon" href={favicon} />
</svelte:head>

{#if theme === 'dark' || theme === 'light'}
	<div class="pointer-events-none fixed inset-0 -z-10 bg-(--dark-bg)"></div>
{:else if disableVideo}
	<img src={THEME_PREVIEWS[theme]} alt="" class="fixed inset-0 -z-10 h-full w-full object-cover" />
{:else}
	{#key theme}
		<video class="fixed inset-0 -z-10 h-full w-full object-cover" src={THEME_VIDEOS[theme]} poster={THEME_PREVIEWS[theme]} autoplay loop muted playsinline></video>
	{/key}
{/if}
{#if theme !== 'dark' && theme !== 'light'}
	<div class="fixed inset-0 -z-10 {THEME_OVERLAYS[theme]} pointer-events-none"></div>
	<div class="pointer-events-none fixed inset-0 -z-10" style="background: radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%);"></div>
{/if}

{#if theme === 'outbreak' || theme === 'overgrown' || theme === 'wizardry'}<div class="film-burn"></div>{/if}
{#if theme === 'noir'}
	<div class="noir-grain pointer-events-none fixed inset-0 -z-10"></div>
	<div class="noir-scratches pointer-events-none fixed inset-0 -z-10"></div>
	<div class="noir-dust pointer-events-none fixed inset-0 -z-10"></div>
{/if}
{#key splashKey}<Splash />{/key}
<TopNav />
{#if showOfflineBanner}
	<div style="top: calc(3.25rem + env(safe-area-inset-top))" class="offline-banner pixel-frame fixed right-0 left-0 z-20 h-10 overflow-hidden">
		<div class="pixel-frame absolute inset-0 bg-(--absent-frame-outer)"></div>
		<div class="pixel-frame absolute inset-0.5 bg-(--absent-frame-highlight)"></div>
		<div class="pixel-frame absolute inset-1 bg-(--absent-frame-shadow)"></div>
		<div class="pixel-frame absolute inset-1.5 bg-(--absent-frame-fill)"></div>
		<div class="relative flex h-full items-center justify-center px-10 text-center text-[0.68rem] leading-tight font-bold text-(--absent-frame-text) uppercase">Offline mode. Co-op, leaderboards, friends, and cloud sync are unavailable.</div>
		<button use:btn type="button" onclick={() => (offlineBannerDismissed = true)} aria-label="Dismiss offline banner" class="absolute top-0 right-1 bottom-0 z-10 flex w-8 cursor-pointer items-center justify-center text-(--absent-frame-text)">
			<Icon icon="mdi:close" class="size-4" />
		</button>
	</div>
{/if}
<main class="mx-auto flex min-h-dvh w-full flex-col" style:padding-top="calc({showOfflineBanner ? '5.75rem' : '3.25rem'} + env(safe-area-inset-top))" style:padding-bottom="calc(3.25rem + env(safe-area-inset-bottom))">
	{@render children()}
</main>
<Toasts />
<BottomNav />
<GameInviteModal />
